import type {
  HTMLAttributes,
  LegacyRef,
  MutableRefObject,
  ReactNode,
  RefObject,
  VideoHTMLAttributes,
} from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import type shaka from 'shaka-player';
import { usePlayerController } from '../hooks/usePlayerController';
import type { AdType, PlayerUIState, SdkPlayerController, UsePlayerControllerConfig } from '../types/player.types';

export type PlayerProps = {
  manifestUrl: string | null;
  drmConfig: UsePlayerControllerConfig['drmConfig'];
  isLiveStream: boolean;
  playlistItem?: PlaylistItem;
  startTime?: number;
  autostart?: boolean;
  adType?: AdType;
  adsConfig?: UsePlayerControllerConfig['adsConfig'];
  mediaKey?: string;
  containerClassName?: string;
  containerProps?: HTMLAttributes<HTMLDivElement>;
  /** Sets `data-testid` on the shell element (React typing-friendly). */
  shellTestId?: string;
  videoClassName?: string;
  videoProps?: VideoHTMLAttributes<HTMLVideoElement>;
  onReady?: (player: SdkPlayerController) => void;
  onBeforePlay?: () => void;
  onTeardown?: () => void;
  /** Rendered inside `<video>` (e.g. `<track>` elements). */
  videoChildren?: ReactNode;
  /** Sidecar JWP thumbnail VTT URL (VOD only). Side-loaded into Shaka after manifest load. */
  trickplayThumbnailsUrl?: string | null;
  /** Sidecar caption/subtitle tracks registered with Shaka after manifest load. */
  sidecarCaptionTracks?: UsePlayerControllerConfig['sidecarCaptionTracks'];
  /** Optional Shaka UI overlay configuration overrides. */
  uiOverrides?: Partial<shaka.extern.UIConfiguration>;
  children?:
    | ReactNode
    | ((ctx: {
        state: PlayerUIState;
        videoRef: RefObject<HTMLVideoElement | null>;
        containerRef: RefObject<HTMLDivElement | null>;
        shakaPlayerRef: MutableRefObject<shaka.Player | null>;
      }) => ReactNode);
};

function mergeClassNames(...parts: Array<string | undefined>): string | undefined {
  const s = parts.filter(Boolean).join(' ');
  return s || undefined;
}

/**
 * Thin React surface: `<video>` + `usePlayerController`. The container `<div>` doubles as the
 * Shaka UI `videoContainer` (Shaka inserts its control bar as a sibling to `<video>` inside it).
 * Layout/overlays go in `children` and stack above the Shaka chrome.
 */
export function Player({
  containerClassName,
  containerProps,
  shellTestId,
  videoClassName,
  videoProps,
  onReady,
  onBeforePlay,
  onTeardown,
  videoChildren,
  children,
  manifestUrl,
  drmConfig,
  isLiveStream,
  playlistItem,
  startTime,
  autostart,
  adType,
  adsConfig,
  mediaKey,
  trickplayThumbnailsUrl,
  sidecarCaptionTracks,
  uiOverrides,
}: PlayerProps) {
  const { videoRef, containerRef, state, shakaPlayerRef } = usePlayerController({
    manifestUrl,
    drmConfig,
    isLiveStream,
    playlistItem,
    startTime,
    autostart,
    adType,
    adsConfig,
    mediaKey,
    onSessionReady: onReady,
    onBeforeLoad: onBeforePlay,
    onTeardown,
    trickplayThumbnailsUrl,
    sidecarCaptionTracks,
    uiOverrides,
  });

  const { className: videoExtraClass, style: videoStyle, ...restVideo } = videoProps ?? {};
  const { className: containerExtraClass, ...restContainer } = containerProps ?? {};

  return (
    <div
      ref={containerRef as LegacyRef<HTMLDivElement>}
      className={mergeClassNames(containerClassName, containerExtraClass)}
      {...restContainer}
      data-testid={shellTestId}
    >
      <video
        ref={videoRef as LegacyRef<HTMLVideoElement>}
        className={mergeClassNames(videoClassName, videoExtraClass)}
        style={{ ...(videoStyle ?? {}) }}
        {...restVideo}
      >
        {videoChildren}
      </video>
      {typeof children === 'function'
        ? children({ state, videoRef, containerRef, shakaPlayerRef })
        : children}
    </div>
  );
}
